import { useCallback, useEffect, useState } from "react";
import * as hrmsService from "../services/hrmsService";
import { EMPTY_HR_ACCESS } from "../constants/hrPermissions";

// One HR access answer per signed-in identity, shared by the sidebar and every HR screen (GET hrms/access).
// It is fetched once per identity and again whenever a screen announces a change (for example the first
// settings save).
//
// The answer belongs to whoever holds the token: a Super Admin assuming a vendor (and leaving again) swaps the
// token without a page reload, so the cache is keyed by it. Without that, the first answer of the page load
// - another vendor's, or the Super Admin's own "no tenant" - was served to every identity after it.
let cached = null; // { key, value }
let inflight = null; // { key, promise }

export const HR_ACCESS_CHANGED = "hr-access-changed";
export const notifyHrAccessChanged = () => {
  cached = null;
  window.dispatchEvent(new Event(HR_ACCESS_CHANGED));
};

const identityKey = () => {
  try {
    return localStorage.getItem("token") || "";
  } catch {
    return "";
  }
};

const fetchOnce = () => {
  const key = identityKey();
  if (cached && cached.key === key) return Promise.resolve(cached.value);
  if (inflight && inflight.key === key) return inflight.promise;
  const promise = hrmsService
    .getAccess()
    .then((a) => {
      const value = a || EMPTY_HR_ACCESS;
      // getAccess() hands back EMPTY_HR_ACCESS itself when the request failed; a real "no HR" answer from the
      // API is a fresh object. Keep only the real ones, so a failed request is retried rather than remembered.
      if (value !== EMPTY_HR_ACCESS) cached = { key, value };
      return value;
    })
    .finally(() => {
      if (inflight && inflight.promise === promise) inflight = null;
    });
  inflight = { key, promise };
  return promise;
};

// Test hook: forget what was fetched.
export const resetHrAccessCache = () => {
  cached = null;
  inflight = null;
};

// `enabled` false (for example a Super Admin, who has no tenant) never calls the API.
export default function useHrAccess(enabled = true) {
  const key = identityKey();
  const [access, setAccess] = useState(cached && cached.key === key ? cached.value : EMPTY_HR_ACCESS);
  const [loaded, setLoaded] = useState((!!cached && cached.key === key) || !enabled);

  const load = useCallback(async () => {
    const a = await fetchOnce();
    setAccess(a);
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    // A different identity than the one the state above was built for: drop its answer until this one arrives.
    setLoaded(!!cached && cached.key === key);
    load();
    window.addEventListener(HR_ACCESS_CHANGED, load);
    return () => window.removeEventListener(HR_ACCESS_CHANGED, load);
  }, [enabled, key, load]);

  // Not enabled: whatever a previous identity left in state must not be shown (for example the vendor's answer
  // after a Super Admin leaves "view as vendor").
  return { access: enabled ? access : EMPTY_HR_ACCESS, loaded: loaded || !enabled, reload: notifyHrAccessChanged };
}
